import { Fragment } from "preact";
import type { TargetedTouchEvent } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import {
  listById,
  listLabel,
  noteHtml,
  required,
  SWIPE_PX,
  styled,
  ToucanBadge,
  ToucanButton,
  ToucanLeafing,
  ToucanLightbox,
} from "toucan";
import { MatchFooter, MatchPicker } from "../components/MatchPicker.tsx";
import { PlayingBars } from "../components/PlayingBars.tsx";
import { ShelfDialog } from "../components/ShelfDialog.tsx";
import { Transport } from "../components/Transport.tsx";
import { removeFromLibrary } from "../library.ts";
import {
  albumFacts,
  albumUri,
  releasedLine,
  setAlbumNote,
  setAlbumShelf,
  UNSHELVED_LABEL,
} from "../model.ts";
import type { Album, ArtistFacts, Artwork, Prose } from "../model.ts";
import { resolveInto } from "../musicbrainz.ts";
import { albumDetail, isEmpty } from "../prose.ts";
import type { AlbumDetail, RecordFacts } from "../prose.ts";
import { releaseDetail } from "../sleeve.ts";
import type { ReleaseDetail } from "../sleeve.ts";
import { albumPlaying } from "../playback.ts";
import type { Playing, TransportCommand } from "../playback.ts";
import { shelves, store } from "../state.ts";

const Page = styled("section")`
  min-height: 100%;
  padding: 1rem;
  /* The warm canvas every routed page sits on. */
  background: var(--toucan-canvas);

  /* One column of cards on a phone, two on a wide screen. The max-width keeps
     prose from running the width of a desktop.

     The two wrappers exist only for the wide layout. Narrow, they collapse to
     display: contents, so every card is a child of one flex column and the
     order property puts them in reading order: the record, your shelf and
     note, then what is known about it. */
  .column {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    max-width: 30rem;
    margin: 0 auto;
  }

  .side,
  .main {
    display: contents;
  }

  .record {
    order: 1;
  }
  .mine {
    order: 2;
  }
  .about {
    order: 3;
  }
  .performer {
    order: 4;
  }
  .artwork {
    order: 5;
  }
  .matched {
    order: 6;
  }

  @media (min-width: 48rem) {
    .column {
      display: grid;
      grid-template-columns: minmax(0, 18rem) minmax(0, 1fr);
      align-items: start;
      gap: 1.5rem;
      max-width: 46rem;
    }

    .side,
    .main {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      min-width: 0;
    }
  }

  /* A card with nothing to show is not rendered, so an album little is known
     about is a shorter page rather than one with holes. */
  .card {
    padding: 1rem;
    border-radius: var(--pico-border-radius);
    background: var(--toucan-surface);
    box-shadow: var(--toucan-shadow-sm);
  }

  .card h3 {
    margin: 0 0 0.6rem;
    font-size: 1rem;
    letter-spacing: -0.02em;
    color: var(--toucan-heading);
  }

  /* The sleeve and the player are one card: no padding, so the player sits
     against the artwork and shares its rounded corners. */
  .record {
    padding: 0;
    box-shadow: var(--toucan-shadow-md);
    overflow: hidden;
  }

  .cover {
    display: grid;
    place-items: center;
    width: 100%;
    aspect-ratio: 1;
    object-fit: cover;
  }

  .cover.placeholder {
    background: var(--toucan-surface-sunken);
    color: var(--toucan-muted);
    font-size: 3rem;
  }

  .play-region {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    padding: 0.75rem;
    background: var(--toucan-surface);
  }

  /* The playing bars lead the title, as on a library tile. */
  .play-region h2 {
    display: flex;
    align-items: baseline;
    gap: 0.35rem;
    margin: 0;
    font-size: 1.15rem;
    line-height: 1.2;
    color: var(--toucan-heading);
  }

  /* The shelf-and-note card. */
  .details {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    min-width: 0;
  }

  /* Wikipedia's summary extract, shown whole. */
  .extract {
    margin: 0;
    font-size: 0.95rem;
    line-height: 1.5;
    color: var(--toucan-ink);
  }

  /* Where a card's material came from, as a mark sized to its line: after
     the text or facts it sourced, or beside the Artwork heading. For
     Wikipedia's licence, see Extract. */
  .source {
    display: inline-grid;
    place-items: center;
    width: 1.15em;
    height: 1.15em;
    vertical-align: -0.2em;
    color: var(--toucan-muted);
  }

  .source svg {
    width: 100%;
    height: 100%;
  }

  .card h3 .source {
    margin-left: 0.4rem;
  }

  /* What the artist is, where they are from and their years, directly under
     the heading, which is their name. */
  .who {
    margin: -0.4rem 0 0.6rem;
    font-size: 0.85rem;
    color: var(--toucan-muted);
  }

  /* When the record came out, above whatever is written about it, labelled
     inline rather than with a heading. */
  .released {
    margin: -0.4rem 0 0.6rem;
    font-size: 0.85rem;
  }

  .released span {
    color: var(--toucan-muted);
  }

  /* Spans rather than a list: a genre row is chips, and a ul inside a card
     inherits whatever markers and indent the CSS framework gives lists. */
  .genres {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.35rem;
    margin-top: 0.6rem;
  }

  /* Hides the row, margin and all, when it holds nothing. */
  .genres:empty {
    display: none;
  }

  .genres span {
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
    background: var(--toucan-surface-sunken);
    font-size: 0.78rem;
    color: var(--toucan-muted);
  }

  /* Grey bars the shape of the text that is coming, while the lookup or the
     search for the record runs. Not on the artwork card, which waits
     differently. Still, not shimmering: the snapshot runner fails a state
     that has an animation running, and it captures an album page. */
  .about.looking span,
  .match.looking span {
    display: block;
    height: 0.7rem;
    margin-bottom: 0.45rem;
    border-radius: 4px;
    background: var(--toucan-surface-sunken);
  }

  .about.looking span:last-child,
  .match.looking span:last-child {
    width: 55%;
    margin-bottom: 0;
  }

  .nothing {
    margin: 0;
    font-size: 0.9rem;
    line-height: 1.5;
    color: var(--toucan-muted);
  }

  /* The page's fine print: the library's dates, placing the record again,
     and removing it. On the canvas under the cards, since it acts on the
     whole album rather than on one card. */
  .matched {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    /* Indented to line up with the text inside the cards. */
    padding: 0 1rem;
    /* Further from the stack than the gap between cards, since it follows
       the whole stack. */
    margin-top: 0.5rem;
    font-size: 0.85rem;
    color: var(--toucan-muted);
  }

  /* What the buttons after it act on. Muted and small, so it labels its part
     of the row without reading as another thing to press. */
  .action-label {
    margin-right: 0.1rem;
  }

  /* More space under an open search than between rows of pills, so the
     Remove row doesn't read as part of it. */
  .matched .match {
    margin-bottom: 0.75rem;
  }

  /* Small pills for fine print, never wrapping inside one, since a label
     broken across two lines reads as two actions. */
  .matched-actions button {
    padding: 0.15rem 0.7rem;
    font-size: 0.875rem;
    white-space: nowrap;
  }

  /* Outline only, since these are rarely needed and a white pill on the
     canvas stands out as something to press. */
  .matched-actions .on-canvas {
    border-color: var(--pico-muted-border-color);
    background: transparent;
  }

  /* One row of pills, wrapping on a narrow phone rather than shrinking: the
     removal is the last of them, furthest from anything reached for often. */
  .matched-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  /* One photograph at a time. The well takes its height from the picture, so
     nothing is cropped or letterboxed, and leafing changes the card's height.
     The minimum gives the waiting placeholder room before a picture loads. */
  .artwork .frame {
    position: relative;
    display: grid;
    place-items: center;
    min-height: 9rem;
    border-radius: var(--pico-border-radius);
    background: var(--toucan-surface-sunken);
    /* The browser keeps the vertical scroll and leaves the sideways swipe to
       useSwipe. */
    touch-action: pan-y;
  }

  /* The picture and the placeholder share one grid cell. */
  .artwork .frame img {
    grid-area: 1 / 1;
    display: block;
    width: 100%;
    height: auto;
    border-radius: var(--pico-border-radius);
  }

  /* Hidden rather than unmounted, so a fetched picture stays decoded for
     leafing back, while adding no height to the well. */
  .artwork .frame img.away {
    display: none;
  }

  /* Where the picture will be, while it loads. Still for the same reason as
     the About card's bars. */
  .artwork .waiting {
    grid-area: 1 / 1;
    width: 3rem;
    height: 3rem;
    border-radius: var(--pico-border-radius);
    background: var(--toucan-border-strong);
    opacity: 0.35;
  }

  /* A transparent button over the whole picture that opens the lightbox. */
  .artwork .bigger {
    position: absolute;
    inset: 0;
    padding: 0;
    border: 0;
    background: none;
    cursor: zoom-in;
  }

  /* Play is the reason to be here, so it takes what width is left over. */
  .play-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .play {
    flex: 1;
  }

  /* Open in Spotify: beside Play until the record starts, then at the end of
     the transport's row. An icon the size of the skips, so it doesn't compete
     with Play. A link, so it is styled as the neutral pill by hand, since the
     house button cannot be an <a>. */
  .elsewhere {
    flex: none;
    display: grid;
    place-items: center;
    width: 44px;
    height: 44px;
    padding: 0;
    border: 1px solid var(--toucan-border-strong);
    border-radius: 999px;
    background: var(--toucan-surface);
    box-shadow: var(--toucan-shadow-sm);
    text-decoration: none;
    /* Spotify's own green, since the mark stands for Spotify rather than for
       a role the theme colours. */
    color: #1db954;
  }

  .elsewhere svg {
    width: 22px;
    height: 22px;
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
  }

  .field textarea {
    margin: 0;
  }

  /* The shelf, Change shelf and Add note in one row, wrapping on a phone
     rather than squeezing the pills. */
  .filing {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  /* Unshelved is a state rather than a shelf, so it is muted text rather than
     a badge. */
  .unshelved {
    color: var(--toucan-muted);
  }

  /* The note in your own hand; everything else on the page is what Spotify or
     MusicBrainz has to say about the record. */
  .note-text {
    margin: 0;
    font-size: 0.95rem;
    line-height: 1.5;
    white-space: pre-wrap;
    color: var(--toucan-ink);
  }

  /* Edit sits at the end of the words, so a one-line note stays one line. */
  .note-edit {
    margin-left: 0.5rem;
    padding: 0.05rem 0.6rem;
    font-size: 0.875rem;
    font-style: normal;
    vertical-align: 0.05em;
  }

  .note-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
  }

  /* The library's own dates for the record, such as when it was added: labels
     in one column and values in another, each as wide as its widest entry.
     The weight and colour undo Pico's for a definition list, so this matches
     the rest of the fine print. */
  .facts {
    display: grid;
    grid-template-columns: max-content max-content;
    gap: 0.2rem 1rem;
    margin: 0;
    font-weight: 400;
    color: inherit;
  }

  .facts dd {
    margin: 0;
  }
`;

/**
 * Spotify's mark: a disc with three arcs across it, drawn rather than fetched
 * so it costs no request and takes the button's own colour.
 */
function SpotifyMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="11" fill="currentColor" />
      <g
        fill="none"
        stroke="var(--toucan-surface)"
        stroke-width="2"
        stroke-linecap="round"
      >
        <path d="M6.4 8.6c4-1.2 8.3-0.7 11.5 1.2" />
        <path d="M7.3 12.3c3.3-1 6.8-0.5 9.5 1" />
        <path d="M8.2 15.8c2.6-0.8 5.4-0.4 7.5 0.9" />
      </g>
    </svg>
  );
}

/**
 * The note, shown as text until Edit and written on Save, so it needs no
 * debounce or save-on-leave. `writing` opens it in the field, for the record
 * that has just been added.
 */
function useNote(album: Album, writing: boolean) {
  const [editing, setEditing] = useState(writing);
  const [draft, setDraft] = useState(album.note ?? "");

  return {
    editing,
    draft,
    text: album.note ?? "",
    start: (): void => {
      setDraft(album.note ?? "");
      setEditing(true);
    },
    type: setDraft,
    cancel: (): void => {
      setEditing(false);
    },
    save: (): void => {
      setAlbumNote(album, draft.trim());
      setEditing(false);
    },
  };
}

/** Where the page has got to in finding out about the record. */
type Lookup =
  | { state: "looking" }
  /** Resolved, and here is what was found — which may itself be empty. */
  | { state: "found"; detail: AlbumDetail }
  /** MusicBrainz answered, and has no record of this album. */
  | { state: "absent" }
  /** MusicBrainz could not be reached. */
  | { state: "failed" }
  /** Nothing to look up: no Spotify id to resolve from and no ids already. */
  | { state: "none" };

/**
 * What is known about the record: resolved to MusicBrainz first if it has no
 * ids yet, then looked up. An album with neither MusicBrainz ids nor a Spotify
 * id is never asked about.
 */
function useAlbumDetail(album: Album): Lookup {
  const [lookup, setLookup] = useState<Lookup>({ state: "looking" });
  const { spotify, mbReleaseGroup, mbArtist } = album.ids;
  const resolved = mbReleaseGroup !== undefined || mbArtist !== undefined;
  useEffect(() => {
    let live = true;
    // Leaving stops the lookup's remaining reads, which are rate limited.
    const leaving = new AbortController();
    // Reset on the way in, or moving between albums would show the first
    // one's prose under the second one's sleeve until the fetch came back.
    setLookup({ state: "looking" });
    const settle = (next: Lookup): void => {
      // The page may have been left while the fetch was out.
      if (live) {
        setLookup(next);
      }
    };
    void (async () => {
      // `resolveLibrary` reaches every album eventually, but the open one
      // should not wait its turn.
      if (!resolved) {
        if (spotify === undefined) {
          settle({ state: "none" });
          return;
        }
        const outcome = await resolveInto(album, Date.now(), leaving.signal);
        if (outcome !== "found") {
          settle({ state: outcome === "absent" ? "absent" : "failed" });
          return;
        }
      }
      const detail = await albumDetail(album, leaving.signal);
      settle(
        detail === null ? { state: "failed" } : { state: "found", detail },
      );
    })();
    return () => {
      live = false;
      leaving.abort();
    };
    // Keyed by what is asked about, so only a change of album refetches.
  }, [album, resolved, spotify, mbReleaseGroup, mbArtist]);
  return lookup;
}

/** How far the release's pictures have got. */
type Coming =
  /**
   * An answer is still expected. Only for an album with a release group: the
   * others start settled.
   */
  | "coming"
  /** Answered, with pictures or with word that there are none. */
  | "settled"
  /** Neither source could be reached, which the card says rather than shows. */
  | "failed";

/** The release's pictures, and how far they have got. */
interface Release {
  detail: ReleaseDetail;
  coming: Coming;
}

/**
 * The original release's pictures, empty until they arrive and for good on an
 * album MusicBrainz cannot name or nobody has photographed. A source that
 * could not be reached is `failed` instead, since opening the page again
 * tries again.
 */
function useRelease(album: Album): Release {
  const named = album.ids.mbReleaseGroup !== undefined;
  const start: Coming = named ? "coming" : "settled";
  const [release, setRelease] = useState<Release>({
    detail: {},
    coming: start,
  });
  const { mbReleaseGroup } = album.ids;
  useEffect(() => {
    let live = true;
    // Reset on the way in, as useAlbumDetail explains.
    setRelease({ detail: {}, coming: start });
    const settle = (found: ReleaseDetail | null): void => {
      // The page may have been left while the fetch was out.
      if (live) {
        setRelease(
          found === null
            ? { detail: {}, coming: "failed" }
            : { detail: found, coming: "settled" },
        );
      }
    };
    void (async () => {
      settle(await releaseDetail(album));
    })();
    return () => {
      live = false;
    };
    // Keyed by what it is fetched from, so only a change of album refetches.
  }, [album, start, mbReleaseGroup]);
  return release;
}

/**
 * One album, where a library tile leads. It shows the cover and the player,
 * the shelf and note, what is known about the record, and Remove. Play gives
 * way to the transport while the record is playing, here or on another
 * device.
 */
export function AlbumPage({
  album,
  playing,
  starting,
  onPlay,
  onCommand,
  onSeek,
  onVolume,
  onNewShelf,
  writingNote,
  onNoteOpened,
}: {
  album: Album;
  playing: Playing | null;
  /** Whether this album has been asked for and Spotify has yet to answer. */
  starting: boolean;
  onPlay: () => void;
  onCommand: (command: TransportCommand) => void;
  onSeek: (position: number) => void;
  onVolume: (percent: number) => void;
  onNewShelf: () => void;
  /** Whether this is the record just added, so the note field opens with it. */
  writingNote: boolean;
  onNoteOpened: () => void;
}) {
  const note = useNote(album, writingNote);
  // Opening the note field is a one-off, so the Shell is told to forget it.
  useEffect(() => {
    if (writingNote) {
      onNoteOpened();
    }
  }, [writingNote, onNoteOpened]);
  const lookup = useAlbumDetail(album);
  const release = useRelease(album);
  // Whether the search by name is open as its fields. Held here because the
  // About card and the fine print's Manual reset button both use it.
  const [manual, setManual] = useState(false);
  const running = albumPlaying(playing, album.id);
  // Play and Open in Spotify both need a Spotify id, so an album without one
  // offers neither.
  const spotifyId = album.ids.spotify;
  const spotify =
    spotifyId === undefined ? null : (
      // No target: a spotify: address opens the Spotify app, and a new tab
      // for it would be left behind empty.
      <a
        class="elsewhere"
        href={albumUri(spotifyId)}
        aria-label="Open in Spotify"
      >
        <SpotifyMark />
      </a>
    );
  return (
    <Page>
      <div class="column">
        <div class="side">
          <div class="card record">
            <Cover album={album} />
            <div class="play-region">
              <h2>
                {running ? (
                  <PlayingBars paused={required(playing, "playing").paused} />
                ) : null}
                {album.name}
              </h2>
              {running ? (
                <Transport
                  playing={required(playing, "playing")}
                  onCommand={onCommand}
                  onSeek={onSeek}
                  onVolume={onVolume}
                >
                  {spotify}
                </Transport>
              ) : (
                <div class="play-row">
                  {/* Says "Starting…" until Spotify reports the play, so the
                    press doesn't look missed. */}
                  {spotifyId === undefined ? null : (
                    <ToucanButton
                      variant="primary"
                      class="play"
                      disabled={starting}
                      onClick={onPlay}
                    >
                      {starting ? "Starting…" : "Play"}
                    </ToucanButton>
                  )}
                  {spotify}
                </div>
              )}
            </div>
          </div>
          <div class="card mine details">
            <h3>Toucan Music</h3>
            <ShelfAndNote album={album} note={note} onNewShelf={onNewShelf} />
          </div>
        </div>
        <div class="main">
          <Lookout
            album={album}
            lookup={lookup}
            manual={manual}
            onMatched={() => {
              setManual(false);
            }}
          />
          {/* Keyed by the release, so another record's gallery starts at its
              first picture. */}
          {release.detail.artwork === undefined ? null : (
            <Gallery
              key={release.detail.release}
              artwork={release.detail.artwork}
              release={required(release.detail.release, "release")}
            />
          )}
          <ComingArtwork coming={release.coming} />
          {/* The page's fine print, styled by the .matched rule. */}
          <div class="matched">
            <dl class="facts">
              {albumFacts(album).map((fact) => (
                <Fragment key={fact.label}>
                  <dt>{fact.label}</dt>
                  <dd>{fact.value}</dd>
                </Fragment>
              ))}
            </dl>
            <MatchFooter
              album={album}
              manual={manual}
              onManual={setManual}
              searchingAbove={lookup.state === "absent"}
            />
            <div class="matched-actions">
              <span class="action-label">Library album</span>
              <ToucanButton
                variant="danger"
                onClick={() => {
                  removeFromLibrary(album.id);
                }}
              >
                Remove
              </ToucanButton>
            </div>
          </div>
        </div>
      </div>
    </Page>
  );
}

/**
 * Spotify's square cover at the top of the page, the same picture as the
 * library tile. The archive's scans have their own card further down.
 */
function Cover({ album }: { album: Album }) {
  if (album.image === undefined) {
    return (
      <div class="cover placeholder" aria-hidden="true">
        ♪
      </div>
    );
  }
  return <img class="cover" src={album.image} alt="" />;
}

/**
 * Turning to the next picture by dragging sideways, as the handlers to spread
 * onto whatever element the pictures are drawn in.
 *
 * A tap and a vertical scroll both end in the same pair of events, so only a
 * deliberate sideways drag counts.
 */
function useSwipe(step: (by: number) => void): {
  onTouchStart: (event: TargetedTouchEvent<HTMLElement>) => void;
  onTouchEnd: (event: TargetedTouchEvent<HTMLElement>) => void;
} {
  const [from, setFrom] = useState(0);
  return {
    onTouchStart: (event) => {
      setFrom(required(event.changedTouches[0], "touch").clientX);
    },
    onTouchEnd: (event) => {
      const moved = required(event.changedTouches[0], "touch").clientX - from;
      if (Math.abs(moved) > SWIPE_PX) {
        step(moved < 0 ? 1 : -1);
      }
    },
  };
}

/**
 * A picture and its neighbours, mounted together so the browser fetches the
 * next and previous while this one is shown. The archive is slow: each address
 * redirects twice, and the scan arrives at full size.
 *
 * The indices wrap, so at either end this also fetches the picture at the
 * other end, although `step` clamps and only a dot reaches it.
 */
function around(index: number, count: number): number[] {
  return [
    ...new Set([index, (index + 1) % count, (index - 1 + count) % count]),
  ];
}

/**
 * Every photograph the Cover Art Archive holds of the chosen pressing, such as
 * the sleeve's two faces and the record's label, one at a time rather than as
 * a grid of thumbnails. Leafing is a swipe, or the arrows and dots of
 * `ToucanLeafing`.
 */
function Gallery({
  artwork,
  release,
}: {
  artwork: Artwork[];
  release: string;
}) {
  const [at, setAt] = useState(0);
  const [open, setOpen] = useState(false);
  // Which pictures have an <img> in the document, and which of those have
  // arrived. A picture keeps its element from then on, so the browser fetches
  // each one once and leafing back to it costs nothing.
  const [reached, setReached] = useState(() => around(0, artwork.length));
  const [arrived, setArrived] = useState<string[]>([]);
  const shown = required(artwork[at], "picture");
  const ready = arrived.includes(shown.url);
  const goTo = (index: number): void => {
    setAt(index);
    setReached((was) => [
      ...new Set([...was, ...around(index, artwork.length)]),
    ]);
  };
  // Clamped rather than wrapped, so you can tell where in the run you are.
  const step = (by: number): void => {
    goTo(Math.min(Math.max(at + by, 0), artwork.length - 1));
  };
  const swipe = useSwipe(step);
  // The same addresses the card draws, so the lightbox opens on pictures the
  // browser already has. A document's page also passes the PDF, which only
  // the lightbox can show.
  const pictures = artwork.map((picture) => ({
    src: picture.url,
    label: picture.label,
    ...(picture.pdf !== undefined ? { pdf: picture.pdf } : {}),
  }));
  return (
    <div class="card artwork">
      <h3>
        Artwork
        {/* To the release's cover art page on MusicBrainz, since the
            archive's own addresses answer with JSON and bare images. */}
        <a
          class="source"
          href={`https://musicbrainz.org/release/${release}/cover-art`}
          target="_blank"
          rel="noreferrer"
          aria-label="This pressing's artwork on the Cover Art Archive"
          title="From the Cover Art Archive"
        >
          <CoverArtMark />
        </a>
      </h3>
      <div class="frame" {...swipe}>
        {/* Every picture reached so far. All but the one shown are `away`. */}
        {reached.map((index) => {
          const picture = required(artwork[index], "picture");
          return (
            <img
              key={picture.url}
              src={picture.url}
              alt={picture.label}
              class={
                index === at && arrived.includes(picture.url) ? "" : "away"
              }
              onLoad={() => {
                setArrived((was) => [...was, picture.url]);
              }}
            />
          );
        })}
        {ready ? null : <span class="waiting" aria-hidden="true" />}
        {/* The whole picture opens the lightbox, since the column is too
            narrow to see a scan properly. */}
        <button
          type="button"
          class="bigger"
          aria-label={`Look at the ${shown.label.toLowerCase()} larger`}
          onClick={() => {
            setOpen(true);
          }}
        />
      </div>
      <ToucanLeafing pictures={pictures} at={at} onGoTo={goTo} onStep={step} />
      {open ? (
        <ToucanLightbox
          pictures={pictures}
          at={at}
          onGoTo={goTo}
          onStep={step}
          onClose={() => {
            setOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * The Artwork card while the archive is being asked, or, if neither source
 * could be reached, saying so. It often goes away with no gallery, but
 * without it a page that looks finished would grow a card a moment later.
 *
 * The wait is the same shallow well and grey square a loading picture has.
 */
function ComingArtwork({ coming }: { coming: Coming }) {
  if (coming === "settled") {
    return null;
  }
  if (coming === "failed") {
    return (
      <div class="card artwork">
        <h3>Artwork</h3>
        <p class="nothing">{NO_ARTWORK}</p>
      </div>
    );
  }
  return (
    <div class="card artwork looking">
      <h3>Artwork</h3>
      <div class="frame">
        <span class="waiting" aria-hidden="true" />
      </div>
    </div>
  );
}

/**
 * The shelf the record is on and your note about it, each with the control
 * that changes it. Neither has a label, since each already says what it is.
 */
function ShelfAndNote({
  album,
  note,
  onNewShelf,
}: {
  album: Album;
  note: ReturnType<typeof useNote>;
  onNewShelf: () => void;
}) {
  const [picking, setPicking] = useState(false);
  const shelf =
    album.shelfId === undefined
      ? undefined
      : listById(store().shelves, album.shelfId);
  return (
    <>
      <div class="filing">
        {shelf === undefined ? (
          <span class="shelf-name unshelved">{UNSHELVED_LABEL}</span>
        ) : (
          <ToucanBadge class="shelf-name" tone="accent">
            {listLabel(shelf)}
          </ToucanBadge>
        )}
        <ToucanButton
          onClick={() => {
            setPicking(true);
          }}
        >
          Change shelf
        </ToucanButton>
        {/* Only while there is no note; after that, Edit follows the words. */}
        {note.text === "" && !note.editing && (
          <ToucanButton onClick={note.start}>Add note</ToucanButton>
        )}
      </div>
      <NoteField note={note} />
      {picking && (
        <ShelfDialog
          shelves={shelves()}
          shelfId={album.shelfId ?? null}
          onPick={(shelfId) => {
            setAlbumShelf(album, shelfId);
          }}
          onNew={onNewShelf}
          onClose={() => {
            setPicking(false);
          }}
        />
      )}
    </>
  );
}

/**
 * The note being written: opened with the caret after the last word, saved by
 * Enter and cancelled by Escape. Shift+Enter makes a new line, as in a chat
 * box.
 */
function NoteEditor({ note }: { note: ReturnType<typeof useNote> }) {
  const field = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const box = required(field.current, "the note field");
    box.focus();
    // At the end, since a note is added to more often than rewritten.
    box.setSelectionRange(box.value.length, box.value.length);
  }, []);
  return (
    <div class="field">
      <textarea
        ref={field}
        rows={2}
        placeholder="Note…"
        value={note.draft}
        onInput={(event) => {
          note.type(event.currentTarget.value);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            note.save();
          } else if (event.key === "Escape") {
            note.cancel();
          }
        }}
      />
      <div class="note-actions">
        <ToucanButton onClick={note.cancel}>Cancel</ToucanButton>
        <ToucanButton variant="primary" onClick={note.save}>
          Save
        </ToucanButton>
      </div>
    </div>
  );
}

/** The note, read or being written, with the one control that changes which. */
function NoteField({ note }: { note: ReturnType<typeof useNote> }) {
  if (note.editing) {
    return <NoteEditor note={note} />;
  }
  if (note.text === "") {
    return null;
  }
  return (
    <p class="note-text">
      <span dangerouslySetInnerHTML={{ __html: noteHtml(note.text) }} />
      <ToucanButton class="note-edit" onClick={note.start}>
        Edit
      </ToucanButton>
    </p>
  );
}

/** The About and artist cards, as the lookup's state calls for. */
function Lookout({
  album,
  lookup,
  manual,
  onMatched,
}: {
  album: Album;
  lookup: Lookup;
  /** Whether the search by name has been asked to open as its fields. */
  manual: boolean;
  /** Called once the search by name has placed the record. */
  onMatched: () => void;
}) {
  if (lookup.state === "looking") {
    return (
      // No `aria-busy`: the CSS framework draws its own spinner for it, which
      // lands on top of the bars that already say the same thing.
      <div class="card about looking">
        <h3>About this record</h3>
        <span />
        <span />
        <span />
      </div>
    );
  }
  if (lookup.state === "found" && !isEmpty(lookup.detail)) {
    return (
      <>
        <RecordCard album={album} record={lookup.detail.record} />
        <ArtistCard artist={lookup.detail.artist} />
      </>
    );
  }
  // No ids to look anything up by: the card shows only the release date the
  // library holds, or nothing if there is none.
  if (lookup.state === "none") {
    return <RecordCard album={album} record={undefined} />;
  }
  if (lookup.state === "found") {
    return (
      <div class="card about">
        <h3>About this record</h3>
        <Released album={album} />
        <p class="nothing">{NOTHING.bare}</p>
      </div>
    );
  }
  // MusicBrainz has no link to this Spotify release, so the card searches by
  // name straight away.
  if (lookup.state === "absent") {
    // The page's one search by name, as in MatchFooter. The key restarts it
    // when Manual reset switches its mode.
    const start = manual ? "editing" : "searching";
    return (
      <div class="card about">
        <h3>About this record</h3>
        <Released album={album} />
        <MatchPicker
          key={start}
          album={album}
          start={start}
          onMatched={onMatched}
        />
      </div>
    );
  }
  return (
    <div class="card about">
      <h3>About this record</h3>
      <Released album={album} />
      <p class="nothing">{NOTHING[lookup.state]}</p>
    </div>
  );
}

/**
 * When the record came out. A fact about the record rather than this
 * library's copy, so it is here and not in the fine print.
 */
function Released({ album }: { album: Album }) {
  const line = releasedLine(album);
  if (line === undefined) {
    return null;
  }
  return (
    <p class="released">
      <span>Released</span> {line}
    </p>
  );
}

/**
 * Explains the empty Artwork card when a source could not be reached. A record
 * nobody has photographed has no card, so without this an outage would look
 * the same as a record with no pictures.
 */
const NO_ARTWORK =
  "Could not reach MusicBrainz or the Cover Art Archive just now — nothing was saved, so opening this page again will try again.";

/**
 * Explains an About card with nothing to show. `bare` is a fact about the
 * record that looking again won't change, and `failed` a failure that it
 * might.
 */
const NOTHING: Record<"bare" | "failed", string> = {
  bare: "MusicBrainz knows this album, but neither it nor Wikipedia has anything written about it.",
  failed:
    "Could not reach MusicBrainz or Wikipedia just now — nothing was saved, so opening this page again will try again.",
};

/**
 * What is known about the record itself. Absent entirely where there is
 * nothing at all to say: no date, and nothing found — offline, unresolved, or
 * an album no encyclopedia has heard of.
 */
function RecordCard({
  album,
  record,
}: {
  album: Album;
  record: RecordFacts | undefined;
}) {
  if (record === undefined && releasedLine(album) === undefined) {
    return null;
  }
  return (
    <div class="card about">
      <h3>About this record</h3>
      <Released album={album} />
      {record === undefined ? null : (
        <>
          {record.prose === undefined ? null : <Extract prose={record.prose} />}
          {/* The MusicBrainz mark follows the genres it sourced, as the W
              follows Wikipedia's text. */}
          <div class="genres">
            {(record.genres ?? []).map((genre) => (
              <span key={genre}>{genre}</span>
            ))}
            <MusicBrainzLink
              path={`release-group/${record.releaseGroup}`}
              what="This record"
            />
          </div>
        </>
      )}
    </div>
  );
}

/** The artist, headed by their own name. */
function ArtistCard({ artist }: { artist: ArtistFacts | undefined }) {
  if (artist === undefined) {
    return null;
  }
  return (
    <div class="card performer">
      <h3>{artist.name}</h3>
      {/* The mark follows the facts it sourced, as in the record card. The
          line is drawn even when MusicBrainz has no facts, to hold it. */}
      <p class="who">
        {artist.line}{" "}
        <MusicBrainzLink path={`artist/${artist.id}`} what={artist.name} />
      </p>
      {artist.prose === undefined ? null : <Extract prose={artist.prose} />}
    </div>
  );
}

/**
 * A Wikipedia extract, with a link to the article. CC BY-SA requires the
 * credit, so the link's label and title name the licence, since the mark
 * alone cannot.
 */
function Extract({ prose }: { prose: Prose }) {
  return (
    <p class="extract">
      {prose.extract}{" "}
      <a
        class="source credit"
        href={prose.url}
        target="_blank"
        rel="noreferrer"
        aria-label={`Read "${prose.title}" on Wikipedia — text CC BY-SA`}
        title="From Wikipedia, CC BY-SA"
      >
        <WikipediaMark />
      </a>
    </p>
  );
}

/** The way back to whichever MusicBrainz entity a card was assembled from. */
function MusicBrainzLink({ path, what }: { path: string; what: string }) {
  return (
    <a
      class="source"
      href={`https://musicbrainz.org/${path}`}
      target="_blank"
      rel="noreferrer"
      aria-label={`${what} on MusicBrainz`}
      title={`${what} on MusicBrainz`}
    >
      <MusicBrainzMark />
    </a>
  );
}

/**
 * Wikipedia's W, drawn inline so it costs no request and takes the link's
 * colour.
 */
function WikipediaMark() {
  return (
    <svg viewBox="0 0 128 128" aria-hidden="true">
      <path
        fill="currentColor"
        d="M120.85 29.21c0 .41-.13.78-.38 1.12-.26.33-.53.5-.84.5-2.49.24-4.54 1.04-6.12 2.41-1.59 1.36-3.22 3.97-4.91 7.81L82.8 99.19c-.17.54-.64.81-1.42.81-.61 0-1.08-.27-1.42-.81L65.49 68.93 48.85 99.19c-.34.54-.81.81-1.42.81-.74 0-1.23-.27-1.47-.81L20.61 41.05c-1.58-3.61-3.25-6.13-5.01-7.56-1.75-1.43-4.2-2.32-7.33-2.66-.27 0-.53-.14-.76-.43-.24-.28-.36-.61-.36-.98 0-.95.27-1.42.81-1.42 2.26 0 4.62.1 7.09.3 2.29.21 4.45.31 6.47.31 2.06 0 4.49-.1 7.29-.31 2.93-.2 5.53-.3 7.79-.3.54 0 .81.47.81 1.42 0 .94-.17 1.41-.5 1.41-2.26.17-4.04.75-5.34 1.72-1.3.98-1.95 2.26-1.95 3.85 0 .81.27 1.82.81 3.03l20.95 47.31 11.89-22.46-11.08-23.23c-1.99-4.14-3.63-6.82-4.91-8.02-1.28-1.19-3.22-1.93-5.82-2.2-.24 0-.46-.14-.68-.43-.22-.28-.33-.61-.33-.98 0-.95.23-1.42.71-1.42 2.26 0 4.33.1 6.22.3 1.82.21 3.76.31 5.82.31 2.02 0 4.16-.1 6.42-.31 2.33-.2 4.62-.3 6.88-.3.54 0 .81.47.81 1.42 0 .94-.16 1.41-.5 1.41-4.52.31-6.78 1.59-6.78 3.85 0 1.01.52 2.58 1.57 4.7l7.33 14.88 7.29-13.61c1.01-1.92 1.52-3.54 1.52-4.86 0-3.1-2.26-4.75-6.78-4.96-.41 0-.61-.47-.61-1.41 0-.34.1-.66.3-.96.21-.31.41-.46.61-.46 1.62 0 3.61.1 5.97.3 2.26.21 4.12.31 5.57.31 1.04 0 2.58-.09 4.6-.26 2.56-.23 4.71-.35 6.43-.35.4 0 .6.4.6 1.21 0 1.08-.37 1.62-1.11 1.62-2.63.27-4.75 1-6.35 2.18-1.6 1.18-3.6 3.86-5.99 8.04l-9.72 17.97 13.16 26.81 19.43-45.18c.67-1.65 1.01-3.17 1.01-4.55 0-3.31-2.26-5.06-6.78-5.27-.41 0-.61-.47-.61-1.41 0-.95.3-1.42.91-1.42 1.65 0 3.61.1 5.87.3 2.09.21 3.85.31 5.26.31 1.49 0 3.21-.1 5.16-.31 2.03-.2 3.85-.3 5.47-.3.47 0 .71.4.71 1.21z"
      />
    </svg>
  );
}

/**
 * The Cover Art Archive's mark: the MetaBrainz hexagon, split down the middle.
 * Both projects use the shape and differ by colour, which these one-colour
 * marks can't show, so this one is outlined and MusicBrainz's is filled.
 */
function CoverArtMark() {
  return (
    <svg viewBox="0 0 25 28" aria-hidden="true">
      <g fill="none" stroke="currentColor" stroke-width="1.6">
        <path d="M11.2 1.4 0.8 7.4v13.2l10.4 6V1.4z" />
        <path d="M13.8 1.4l10.4 6v13.2l-10.4 6V1.4z" />
      </g>
    </svg>
  );
}

/** MusicBrainz's own icon: the two halves of its hexagon, in one colour. */
function MusicBrainzMark() {
  return (
    <svg viewBox="0 0 27 30" aria-hidden="true">
      <path fill="currentColor" d="m13 1-12 7v14l12 7z" />
      <path fill="currentColor" d="m14 1 12 7v14l-12 7z" />
    </svg>
  );
}
